import classNames from "classnames";
import { Entry } from "contentful";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";

import { useBreakpoint, useWindowSize } from "~/contexts/breakpoint";
import { useGiftCardMedia } from "~/contexts/giftcard-media";
import CrossCountry from "~/types/cross-country";
import Editorials from "~/types/editorials";
import { basicAnimationStates } from "~/utils/animation-utils";
import Constants from "~/utils/constants";
import imageUtils from "~/utils/image-utils";
import productUtils from "~/utils/product-utils";
import TrackingUtils from "~/utils/tracking-utils";

import EscapeNewLine from "../common/escape-new-line";
import Pane from "../common/pane";
import AccordionShadeSelector from "./accordion-shade-selector";
import styles from "./product-shade-selector.module.scss";

function isVirtualGiftCard(
  el: CrossCountry.Product | Entry<Editorials.VirtualCard>
): el is Entry<Editorials.VirtualCard> {
  return "sys" in el;
}

type ShadeSelectorProps = {
  open: boolean;
  shades: CrossCountry.Product[] | Entry<Editorials.VirtualCard>[] | undefined;
  shadesOOS?: Record<string, boolean>;
  selectedShade: CrossCountry.ChildrenProduct;
  root: CrossCountry.Product;
  minHeight?: number;
  className?: string;
};

export function ShadeSelectorWrapper(props: ShadeSelectorProps) {
  const shadesRef = useRef<HTMLDivElement>(null);
  const { selectedIndex, setSelectedIndex } = useGiftCardMedia();

  const [minHeight, setMinHeight] = useState(props.minHeight ?? 0);

  useEffect(() => {
    if (shadesRef.current && !!props.minHeight) {
      let firstChild = shadesRef.current.children[0];
      if (firstChild) {
        setMinHeight(firstChild.scrollHeight);
      }
    }
  }, [props.minHeight, props.selectedShade]);

  const cssWrapperShades = useMemo(() => {
    if (!props.minHeight) {
      return {};
    }
    if (props.open) {
      return { height: shadesRef.current?.offsetHeight ?? 0 };
    }
    return { height: minHeight };
  }, [minHeight, props]);

  return (
    <div className={styles.wrapperShades} style={cssWrapperShades}>
      <div
        id={Constants.PDP_SHADES_PANEL}
        ref={shadesRef}
        className={classNames(styles.shadesSelector, props.className)}
      >
        {props.shades?.map((shade, i) => {
          // giftcard custom selectro
          if (isVirtualGiftCard(shade)) {
            const currentPreviewUrl = shade.fields.shadePreview.fields
              ? "https:" + shade.fields.shadePreview.fields.file.url
              : undefined;
            return (
              <div key={shade.sys.id} className={styles.shadeSelectorButton}>
                <button
                  role="link"
                  aria-label={shade.fields.cardImage.fields.title}
                  className={i === selectedIndex ? styles.selectedShadeSelectorButton : styles.shadeSelectorButton}
                  onClick={() => {
                    setSelectedIndex(i);
                  }}
                >
                  <div className={styles.shadeImageWrapper}>
                    <Image
                      className={styles.shadeImage}
                      alt={props.selectedShade.display_value}
                      src={currentPreviewUrl ?? ""}
                      height={28}
                      width={28}
                      loader={imageUtils.contentfulImageLoader}
                      aria-hidden
                    />
                  </div>
                </button>
                <div className={styles.shadeSelectorCode}>
                  <p>{i + 1}</p>
                </div>
              </div>
            );
          }
          return (
            <Link
              key={shade.product_id}
              role="radio"
              scroll={false}
              shallow
              aria-label={shade.display_value}
              aria-checked={shade.product_id === props.selectedShade?.product_id}
              aria-disabled={props.shadesOOS?.[shade.product_id]}
              href={`/p/${shade.slug ?? props.root.slug}-${shade.product_id}`}
              className={
                shade.product_id === props.selectedShade?.product_id
                  ? styles.selectedShadeSelectorButton
                  : styles.shadeSelectorButton
              }
              onClick={() => {
                TrackingUtils.track(
                  TrackingUtils.mapEcommerceEvent("view_item", [TrackingUtils.mapEcomItem(props.root, shade)], {
                    user_action: "detail_change_color",
                  })
                );
              }}
            >
              <div className={styles.shadeImageWrapper}>
                {props.shadesOOS?.[shade.product_id] && <span className={styles.outOfStock} />}
                <Image
                  className={styles.shadeImage}
                  alt={shade.display_value}
                  src={productUtils.getProductDroplet(shade?.product_media?.media)?.url || imageUtils.transparentPixel}
                  height={28}
                  width={28}
                  loader={imageUtils.evaImageLoader}
                />
              </div>
              <div className={styles.shadeSelectorCode} aria-hidden>
                <p>{shade.shade_number ?? shade.color?.split(" ")?.[0] ?? ""}</p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

type ProductShadeProps = {
  root: CrossCountry.Product;
  shades: CrossCountry.Product[] | Entry<Editorials.VirtualCard>[];
  shadesOOS?: Record<string, boolean>;
  selectedShade: CrossCountry.ChildrenProduct;
};

export default function ProductShadeSelector(props: ProductShadeProps) {
  const t = useTranslations();

  const windowSize = useWindowSize();
  const breakpoint = useBreakpoint();
  const { selectedIndex, mediaUrl: mediaGiftCardUrl } = useGiftCardMedia();

  const [shadesExpanded, setShadesExpanded] = useState(false);
  const [isOpenAccordion, setIsOpenAccordion] = useState(false);

  const isGiftCard = productUtils.isGiftCard(props.selectedShade);

  const currentShadeName = useMemo(() => {
    if (isGiftCard) {
      const indexedShade = props.shades[selectedIndex];

      if (indexedShade && isVirtualGiftCard(indexedShade)) {
        return <EscapeNewLine text={indexedShade.fields.name} />;
      }
    }

    const { shade_name, shade_number, color } = props.selectedShade;

    if (shade_number && color) {
      if (shade_name) {
        return (
          <>
            <span className={styles.colorNameBold}>
              {shade_number} {shade_name}
            </span>{" "}
            - {color}
          </>
        );
      }
      return (
        <>
          <span className={styles.colorNameBold}>{shade_number}</span> {color}
        </>
      );
    }

    return <>{color}</>;
  }, [props.selectedShade, props.shades, selectedIndex, isGiftCard]);

  const currentShadesPerBrekpoint = useMemo(() => {
    if (!windowSize.width || windowSize.width >= 1920) {
      return 10;
    } else if (windowSize.width >= 1600) {
      return 9;
    } else if (windowSize.width >= 1280) {
      return 8;
    } else if (windowSize.width >= 1090) {
      return 7;
    } else if (windowSize.width >= 900) {
      return 5;
    } else if (windowSize.width >= 640) {
      return 15;
    } else if (windowSize.width >= 480) {
      return 10;
    } else {
      return 8;
    }
  }, [windowSize]);

  const handleAccordionChange = (isOpen: boolean) => {
    setIsOpenAccordion(isOpen);
  };

  return (
    <Pane className={isOpenAccordion && breakpoint === "desktop" ? styles.panePosition : undefined}>
      {!isGiftCard ? (
        <div className={styles.desktopOnly}>
          <AccordionShadeSelector
            version="v2"
            root={props.root}
            selectedShade={props.selectedShade}
            shades={props.shades as CrossCountry.Product[]}
            shadesOOS={props.shadesOOS}
            hideAccordion={false}
            onAccordionChange={handleAccordionChange}
          />
        </div>
      ) : null}
      <div className={classNames(styles.shadesHeader, styles.mobileOnly)}>
        <p className={styles.colorName}>{currentShadeName}</p>
      </div>
      <div className={isOpenAccordion && breakpoint === "desktop" ? styles.hidden : undefined}>
        <ShadeSelectorWrapper
          minHeight={mediaGiftCardUrl ? 41 : 74}
          root={props.root}
          selectedShade={props.selectedShade}
          shades={props.shades}
          shadesOOS={props.shadesOOS}
          open={shadesExpanded}
        />
      </div>

      {props.shades && props.shades.length > currentShadesPerBrekpoint && (
        <AnimatePresence mode="wait">
          <motion.button
            key={`shades-toggle-${shadesExpanded}`}
            initial={basicAnimationStates.off}
            animate={basicAnimationStates.on}
            exit={basicAnimationStates.off}
            transition={{ duration: 0.3 }}
            className={classNames(
              styles.shadesCount,
              isOpenAccordion && breakpoint === "desktop" ? styles.hidden : undefined
            )}
            onClick={() => setShadesExpanded(!shadesExpanded)}
          >
            {shadesExpanded ? t("generic.showless") : t("generic.shadescount", { count: props.shades?.length })}
          </motion.button>
        </AnimatePresence>
      )}
    </Pane>
  );
}
